/**
 * Ableton Device Control - the pure decisions.
 *
 * A Button with `actionType: 'abletonDevice'` toggles ONE named device on one
 * named track, and lights from what the device actually reports rather than
 * from a local latch (DEC-205's rule, applied a second time).
 *
 * **Nothing here talks to a network, and that is the point.** The fault ladder
 * and the lit test are the two decisions in this feature with real ordering
 * and real edge cases in them, so they live in a pure module that a smoke can
 * drive directly - the same split `obs/scenes.ts` uses, and for the same
 * reason: a decision that lives only inside a JSX closure is a decision that
 * never gets tested.
 *
 * ## Status: the readback half is LIVE, with one rung of it still dark
 *
 * `OP-ButtonsDevice v0.1-rc2` is built and loaded in Live, OPBridge ingests
 * `/OP-ButtonsDevice/` and serves `GET /devices/status` (their DEC-584,
 * scope bundle v16), and `ableton/devicePoll.ts` populates
 * {@link AbletonDeviceState} from it. A button now lights from what the
 * device reports.
 *
 * **What is still dark, measured rather than assumed:** the `no_match` and
 * `ambiguous` verdicts never arrive, because OPBridge's parser and the
 * device's emissions disagree on arity. See
 * {@link OPBRIDGE_NO_MATCH_ARITY_FIXED_IN} for the reproduction and the
 * consequence. The arity defect is fixed and deployed as of `v1.15.131`, so
 * both verdicts now arrive; what stands regardless is that a reading's
 * ABSENCE is not evidence, so this module reads a reported state and never
 * infers one.
 *
 * `unknown` remains deliberately NOT a fault - it yields `null` from
 * {@link abletonDeviceFault}, because not having asked is not evidence of
 * anything, and a button that went loud before its first read would cry wolf
 * on every load.
 *
 * Both sibling asks, the wire itself, and why name-based addressing is only
 * safe WITH readback: `.notepads/ableton-device-control-plan.md`.
 */

import type { AbletonDeviceTarget } from '../layout/projectStore.ts';

/**
 * How well we know a bridge's Ableton device state.
 *
 * Same five-value shape as `ObsStatus`, kept deliberately parallel: the
 * distinction that matters in both is between "we read it and it is not
 * there" and "we could not read it", because those send the operator to
 * different places to fix different problems.
 */
export type AbletonDeviceStatus = 'unknown' | 'ok' | 'unauthorized' | 'unavailable' | 'error';

/**
 * How one query resolved on the Ableton side, as OPBridge reports it.
 *
 * Carried through from the wire rather than re-derived here. The earlier
 * version of this module inferred `no-match` from a reading's ABSENCE, which
 * measurement showed to be wrong in both directions - see
 * {@link OPBRIDGE_NO_MATCH_ARITY_FIXED_IN}.
 */
export type AbletonDeviceResolution = 'resolved' | 'no-match' | 'ambiguous';

/** One device query and its outcome, as the M4L device reported it. */
export interface AbletonDeviceReading {
  /** Track token as the device echoed it. Already `san()`-ed - see
   *  {@link deviceKey}. */
  trackName: string;
  /** For `resolved`, the RESOLVED device's own name (which may differ from
   *  the filter that found it - the device matches on name OR class, exact
   *  first then substring). For the other two, the filter we asked with.
   *  Already `san()`-ed. */
  deviceName: string;
  /** 1-based occurrence, matching the device's own convention. */
  nth: number;
  /** How the query resolved. */
  state: AbletonDeviceResolution;
  /** Power state. Present ONLY on `resolved` - OPBridge omits it entirely
   *  otherwise, because there is no device whose power it could describe. */
  on?: boolean;
  /** How many devices matched. Present only on `ambiguous`. */
  count?: number;
}

export interface AbletonDeviceState {
  status: AbletonDeviceStatus;
  readings: AbletonDeviceReading[];
}

/**
 * ✅ FIXED AND DEPLOYED 2026-09-20. Kept as a record, because the SHAPE of the
 * bug is the reason this module reads a reported state instead of inferring
 * one, and that design outlives the bug.
 *
 * **What it was.** The device appends a fourth `track|device` SCOPE atom to
 * `no_match` and `ambiguous` (`OP-ButtonsDevice.js`, `emitNoMatch` at :141).
 * OPBridge parsed both expecting one trailing NUMERIC, read the trailing
 * `"device"` string where it wanted the int, and discarded the message. Two
 * of the four addresses reached nothing.
 *
 * **Measured, twice, on the running daemon - and the second run is the point.**
 * On `v1.15.130+5c0b2a7`: `device_on` and both 3-atom spec shapes LANDED,
 * both 4-atom real shapes DROPPED, `generation` 0 -> 3 for five sends. After
 * OPBridge shipped `split_trailing_scope` (`a47a1125`, v1.15.131), the SAME
 * probe against `v1.15.131+1ff534a` landed all five, `generation` 0 -> 5.
 * The source fix and the running binary were a separate question and the
 * first re-probe, run before the release reached this box, still showed the
 * drop.
 *
 * **Closed by the REAL device, not by our probe.** OPBridge drove a loaded
 * `v0.1-rc2` with its own `get_device_on` read verb and observed
 * `no_match scope=device`, `no_match scope=track` and
 * `ambiguous count=2 nth=0 scope=track` all arriving from the genuine
 * emitter. Our `nth` correction is confirmed on the wire.
 *
 * ⚠️ **What has NOT changed, and must not be "simplified" away now the bug is
 * gone:** {@link abletonDeviceFault} still refuses to infer `device-missing`
 * from an ABSENT entry. Absence never meant "not in Live" and still does not
 * - it means "no reply seen YET", because the poll asks and the answer
 * arrives later. The fix removed one cause of absence; it did not make
 * absence evidence.
 */
export const OPBRIDGE_NO_MATCH_ARITY_FIXED_IN = 'v1.15.131';

/**
 * Normalise a name the way the M4L device does, so our matching agrees with
 * the resolution that actually happened.
 *
 * ⚠️ NOT cosmetic, and NOT a case-folding convenience. `OP-ButtonsDevice.js`
 * puts every name on the wire through `san()` (:101) - **every character
 * outside `[A-Za-z0-9._>#+-]` becomes `_`**, because Max messages choke on
 * spaces. So a device the operator sees in Live as `Auto Filter` arrives here
 * as `Auto_Filter`, and the previous VERBATIM comparison could never match it.
 * The device then compares with `key()` (:112), which is `san()` plus
 * lower-case; we mirror that exactly so a name we call a match is a name the
 * device would also have called a match.
 *
 * This also settles the spaced-track-name contract question OPBridge could not
 * answer for itself: the device never emits a spaced symbol in the first place.
 * And the INBOUND half is MEASURED, not just read (2026-09-23, on the Mac): a
 * `get_device_on` for track `PNO 2` resolved the track (`no_match` scope=device,
 * echoed as `PNO_2`), while a made-up track came back scope=track as the control.
 */
export function deviceKey(s: string): string {
  let out = '';
  for (const c of String(s)) out += /[A-Za-z0-9._>#+-]/.test(c) ? c : '_';
  return (out.length ? out : '_').toLowerCase();
}

/** The state every caller passes until the readback wire lands. The object is
 *  frozen so a consumer cannot repoint `status` on the shared instance; the
 *  array is a plain empty one, so treat this as a default to READ, never as an
 *  accumulator to push into. */
export const UNREAD_DEVICE_STATE: AbletonDeviceState = Object.freeze({
  status: 'unknown' as const,
  readings: [] as AbletonDeviceReading[],
});

/** `nth` is 1-based and absent means the first match. One place decides
 *  that, so the ladder and the lit test cannot disagree about it. */
export function effectiveNth(target: AbletonDeviceTarget | undefined): number {
  const n = target?.nth ?? 1;
  return n >= 1 ? n : 1;
}

/**
 * Readings for this target's track and device name, in wire order.
 *
 * Mirrors the device's own `resolveDevice` (`OP-ButtonsDevice.js` :238)
 * rather than inventing a second matching rule: {@link deviceKey} on both
 * sides, **exact matches preferred, substring only when nothing matched
 * exactly**. That ordering is load-bearing on the device and so it is here -
 * a track literally called `Bass` must not be shadowed by `Bass Sub`.
 *
 * The asymmetry worth knowing: a `resolved` reading carries the RESOLVED
 * device's name, while the operator stored a FILTER. Those are equal in the
 * ordinary case (the operator typed the name) and differ when they typed a
 * substring, which is why the substring arm exists here at all.
 */
function matchesFor(target: AbletonDeviceTarget, st: AbletonDeviceState): AbletonDeviceReading[] {
  const wantTrack = deviceKey(target.trackName);
  const wantName = deviceKey(target.deviceName);
  const onTrack = st.readings.filter((r) => deviceKey(r.trackName) === wantTrack);
  const exact = onTrack.filter((r) => deviceKey(r.deviceName) === wantName);
  if (exact.length > 0) return exact;
  return onTrack.filter((r) => deviceKey(r.deviceName).includes(wantName));
}

/**
 * Why an Ableton Device button cannot fire, or `null` if nothing is wrong.
 *
 * THE ORDER IS THE CONTENT. Each rung is a precondition for reading the next
 * one honestly:
 *
 *   no-bridge       the operator has not said WHICH COMPUTER runs Ableton.
 *                   This outranks every naming rung for the same reason the
 *                   OBS ladder puts its machine first - tracks and devices
 *                   belong to a machine, so asking for a device name while
 *                   the machine is unchosen names step 2 while step 1 is
 *                   owed. It is ALSO why dispatch must refuse an unaimed
 *                   button rather than fall back to the default bridge, and
 *                   here that refusal is load-bearing in a way OBS's was not:
 *                   the OSC target is `127.0.0.1:<device port>`, which
 *                   resolves on
 *                   EVERY machine, so a send to the wrong bridge is an error
 *                   nowhere in the stack. OPBridge answers `{ok:true}` and
 *                   UDP drops it. OBS at least got a 503.
 *   no-track        aimed at a machine, but no track named.
 *   no-device       track named, device not. Ordered after no-track because a
 *                   device name is only meaningful once its track is known -
 *                   the same device name can sit on several tracks.
 *   unauthorized    we were not allowed to read the state, so we cannot know
 *                   whether the device exists. Saying "device not in Live"
 *                   here would send the operator to Ableton to fix a problem
 *                   that lives in OPBridge's scope bundle.
 *   unavailable     nothing is reporting - the M4L device is not loaded, or
 *                   is the silently-deaf second instance that lost the port
 *                   bind. Same reasoning, different owner.
 *   error           any other read failure; same reasoning again.
 *   device-missing  ONLY reachable on `ok`: we really did read the state and
 *                   the name really is not there. This is the rung that makes
 *                   name-based addressing safe - it is what a renamed or
 *                   deleted device looks like, instead of a button that
 *                   silently does nothing at a gig.
 *   ambiguous       ONLY reachable on `ok`: the name matches, but fewer times
 *                   than `nth` asks for. Refusing beats picking - a silent
 *                   pick is a WRONG device, and `live_control.py` raises on
 *                   exactly this rather than guessing.
 *
 * `unknown` yields `null`, not a fault - see the module doc comment.
 */
export type AbletonDeviceFault =
  | 'no-bridge'
  | 'no-track'
  | 'no-device'
  | 'unauthorized'
  | 'unavailable'
  | 'error'
  | 'device-missing'
  | 'ambiguous';

export function abletonDeviceFault(
  target: AbletonDeviceTarget | undefined,
  bridgeIds: string[] | undefined,
  st: AbletonDeviceState,
): AbletonDeviceFault | null {
  if (!bridgeIds || bridgeIds.length === 0) return 'no-bridge';
  if (!target?.trackName) return 'no-track';
  if (!target.deviceName) return 'no-device';
  if (st.status === 'unauthorized') return 'unauthorized';
  if (st.status === 'unavailable') return 'unavailable';
  if (st.status === 'error') return 'error';
  if (st.status === 'ok') {
    const matches = matchesFor(target, st);
    // ⚠️ ABSENCE IS NOT EVIDENCE - see OPBRIDGE_NO_MATCH_ARITY_FIXED_IN.
    // An empty match set means "no reply seen YET" - the poll asks and the
    // answer arrives later - so reporting `device-missing` here would put
    // "not in Live" under a device that is very often present and simply
    // unread, the lying badge in the opposite direction. Still true now the
    // arity bug is fixed: that removed one CAUSE of absence, it did not turn
    // absence into evidence.
    if (matches.length === 0) return null;
    // Read the resolution the device actually reported. Both rungs below were
    // unreachable until OPBridge's arity fix landed (v1.15.131); they now
    // fire from a real reported verdict, with no change needed here.
    const nth = effectiveNth(target);
    const pick = matches[nth - 1];
    if (pick) {
      if (pick.state === 'no-match') return 'device-missing';
      if (pick.state === 'ambiguous') return 'ambiguous';
    } else if (matches.some((m) => m.state === 'ambiguous')) {
      return 'ambiguous';
    } else {
      // Fewer distinct matches than `nth` asks for. This one IS reachable
      // today: it is our own bookkeeping over readings we have seen, not a
      // verdict the device had to send us.
      return 'ambiguous';
    }
  }
  return null;
}

/** Face text for a fault. Short enough to read on a dark stage; the
 *  Inspector carries the long version, and each string names the OWNER of
 *  the failure rather than just its symptom. */
export function abletonDeviceFaultLabel(fault: AbletonDeviceFault): string {
  switch (fault) {
    case 'no-bridge':      return 'pick a computer';
    case 'no-track':       return 'pick a track';
    case 'no-device':      return 'pick a device';
    case 'unauthorized':   return 'no device access';
    case 'unavailable':    return 'device not loaded';
    case 'error':          return 'device error';
    case 'device-missing': return 'not in Live';
    case 'ambiguous':      return 'name not unique';
  }
}

/**
 * Is this button's device currently ON?
 *
 * Only ever true on a status of `ok`. An unread, unauthorized or unreporting
 * bridge reads false - not because the device is known to be off, but because
 * we do not know, and an unlit button is the honest rendering of "we do not
 * know" while a lit one would be a claim.
 *
 * Returns false for an ambiguous or missing name too: with no single match
 * there is no single state, and lighting from the first of several would be
 * exactly the silent wrong-device pick the `ambiguous` rung exists to refuse.
 */
export function abletonDeviceIsOn(
  target: AbletonDeviceTarget | undefined,
  st: AbletonDeviceState,
): boolean {
  if (!target?.trackName || !target.deviceName) return false;
  if (st.status !== 'ok') return false;
  const matches = matchesFor(target, st);
  const nth = effectiveNth(target);
  if (matches.length < nth) return false;
  const pick = matches[nth - 1]!;
  // `on` is ABSENT, not `false`, on a non-resolved entry - OPBridge omits the
  // field rather than defaulting it, deliberately, so that "we could not
  // resolve this name" can never read as "the device is switched off". Honour
  // that: only a `resolved` entry can light a button.
  if (pick.state !== 'resolved') return false;
  return pick.on === true;
}

/** Operator-facing one-liner for a configured target, e.g.
 *  `Vocals / Reverb` or `Vocals / Reverb (2nd)`. Empty string when
 *  unconfigured - the caller shows a fault label instead. */
export function abletonDeviceSummary(target: AbletonDeviceTarget | undefined): string {
  if (!target?.trackName || !target.deviceName) return '';
  const nth = effectiveNth(target);
  const suffix = nth === 1 ? '' : ` (${ordinal(nth)})`;
  return `${target.trackName} / ${target.deviceName}${suffix}`;
}

function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:  return `${n}st`;
    case 2:  return `${n}nd`;
    case 3:  return `${n}rd`;
    default: return `${n}th`;
  }
}
